import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Package, Clock } from 'lucide-react';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  preparing: 'bg-blue-100 text-blue-800',
  packed: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/orders').then(r => { setOrders(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="orders-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl sm:text-4xl font-serif font-medium mb-8">My Orders</h1>

      {orders.length === 0 ? (
        <div data-testid="no-orders" className="text-center py-20">
          <Package className="h-16 w-16 mx-auto text-muted-foreground/30 mb-6" />
          <p className="text-muted-foreground text-lg">No orders yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.order_id} data-testid={`order-${order.order_id}`}
              className="bg-card rounded-3xl border border-border/50 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-semibold text-sm">{order.order_id}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" />
                    {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <Badge className={`${statusColors[order.status]} border-0 rounded-full px-3 py-1 text-xs font-medium capitalize`}>
                  {order.status}
                </Badge>
              </div>
              <div className="space-y-2">
                {order.items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    {item.image && <img src={item.image} alt="" className="w-10 h-10 rounded-xl object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.product_name}</p>
                      <p className="text-xs text-muted-foreground">{item.variant_name} x{item.quantity}</p>
                    </div>
                    <span className="font-medium shrink-0">{'\u20B9'}{item.price * item.quantity}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-border">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="text-lg font-semibold">{'\u20B9'}{order.total}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
